// src/pages/agent/ContractPreview.js
// Renders any of the 5 contract templates with sample (or real) data so the
// agent can verify the document looks polished before sending to a seller.
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CONTRACT_META } from '../../lib/contracts/templates';
import { renderContractHtml, sampleFields } from '../../lib/contracts/render';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Btn, BORDER, BURG, BURG_DK, GOLD, LIGHT, MID } from '../../components/agent/UI';
import ProtectedView from '../../components/agent/ProtectedView';

export default function ContractPreview() {
  const navigate = useNavigate();
  const { profile } = useAgentAuth();
  const { type: typeParam } = useParams();
  const [params] = useSearchParams();
  const initial = typeParam || params.get('type') || 'psa';
  const [type, setType] = useState(CONTRACT_META[initial] ? initial : 'psa');

  const html = useMemo(() => {
    const fields = sampleFields(type);
    return renderContractHtml({
      type,
      fields,
      documentId: 'PREVIEW-' + Math.random().toString(36).slice(2, 8).toUpperCase(),
      parties: CONTRACT_META[type].signers.map(s => ({ ...s, name: fields[`${s.role}Name`] || '' }))
    });
  }, [type]);

  const print = () => {
    const w = window.open('', '_blank');
    if (!w) return alert('Pop-ups blocked — please allow pop-ups to print.');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <>
      <PageHeader
        eyebrow="Document preview"
        title="Contract preview"
        sub="See exactly what your seller, buyer, or JV partner will receive. Sample data is filled in."
        right={
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="ghost" onClick={() => navigate('/agent/contracts')}>← Templates</Btn>
            <Btn variant="ghost" onClick={print}>Print / PDF</Btn>
          </div>
        }
      />

      <div style={s.toolbar}>
        <div style={s.toolbarLabel}>Template</div>
        <div style={s.tabs}>
          {Object.values(CONTRACT_META).map(c => (
            <button
              key={c.id}
              onClick={() => setType(c.id)}
              style={{ ...s.tab, ...(type === c.id ? s.tabActive : {}) }}
            >{c.label}</button>
          ))}
        </div>
      </div>

      <ProtectedView viewer={profile} label="HomeIQa · Confidential">
        <div style={s.previewWrap}>
          <iframe
            title={`Preview · ${CONTRACT_META[type].label}`}
            srcDoc={html}
            sandbox=""
            style={s.iframe}
          />
        </div>
      </ProtectedView>

      <div style={s.note}>
        <strong style={{ color: BURG_DK }}>Note:</strong> Field values shown are sample data for preview.
        When you generate a contract from a lead's Contracts panel, the real lead values are merged in.
        Empty fields render as the classic underlined placeholder so signers can see what still needs
        clarification.
      </div>
    </>
  );
}

const s = {
  toolbar: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18, flexWrap: 'wrap' },
  toolbarLabel: { fontSize: 11, fontWeight: 500, color: MID, letterSpacing: '0.04em', textTransform: 'uppercase' },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tab: { background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '7px 14px', fontSize: 12, color: MID, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  tabActive: { background: BURG, color: '#fff', borderColor: BURG },
  previewWrap: { background: '#E8E0D5', borderRadius: 8, padding: 0, overflow: 'hidden', border: `1px solid ${BORDER}` },
  iframe: { display: 'block', width: '100%', height: '1400px', border: 'none', background: '#E8E0D5' },
  note: { marginTop: 16, fontSize: 12, color: LIGHT, lineHeight: 1.6, padding: '12px 16px', background: '#FAF7F2', border: `1px solid ${BORDER}`, borderRadius: 6 }
};
